import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import DataTable from "../../../components/atoms/table/DataTable.atom"
import Column from "../../../components/atoms/table/Column.atom"
import Button from "../../../components/atoms/Button.atom"
import { DateTime } from "luxon"
import UnifiedDatePicker from "../../../components/compounds/UnifiedDatePicker.compound"
import ColumnGroup from "antd/es/table/ColumnGroup"
import RowColumnGroup from "../../../components/atoms/table/RowColumnGroup.atom"
import InputTextForm from "../../../components/molecules/InputTextForm.molecule"
import ErrorMessageForm from "../../../components/atoms/ErrorMessageForm.atom"
import { clsx } from "../../../utils/strings.utils"
import { compareDates, isDateAtWeekend } from "../../../utils/date.utils"
import useToast from "../../../hooks/useToast.hook"
import {
  DEFAULT_NEW_HOLIDAY,
  HOLIDAYS_MODALS_TABS,
} from "../../../consts/modules/vacations.consts"

const HolidaysTable = ({
  value: initialValue,
  allHolidays = [],
  deletableRows = false,
  newValuePlaceholder = "Agrega un nuevo valor",
  onChange,
  loading,
  year,
}) => {
  const [holidays, setHolidays] = useState(initialValue)
  const [newHoliday, setNewHoliday] = useState(DEFAULT_NEW_HOLIDAY)
  const [editingDateCell, setEditingDateCell] = useState(null)
  const [errors, setErrors] = useState({
    creation: null, // "error message"
    edition: {}, // { [id]: "error message" }
  })
  const datePickerRef = useRef(null)
  const tableRef = React.useRef()

  const { setErrorMessage } = useToast()
  useEffect(() => {
    setHolidays(initialValue)
  }, [initialValue])

  const setCreationError = (errorMessage) => {
    setErrors((prev) => ({ ...prev, creation: errorMessage }))
  }

  const clearCreationError = () => {
    setErrors((prev) => ({ ...prev, creation: null }))
  }

  const setEditionError = (id, errorMessage) => {
    setErrors((prev) => ({
      ...prev,
      edition: {
        ...prev.edition,
        [id]: errorMessage,
      },
    }))
  }

  const clearEditionError = (id) => {
    setErrors((prev) => {
      const { [id]: _, ...rest } = prev.edition
      return { ...prev, edition: rest }
    })
  }

  const isDateRepeated = (date, excludeId = null) =>
    holidays.some((h) => h.date === date && h.id !== excludeId)
  const isNameRepeated = (name, excludeId = null) =>
    holidays.some((h) => h.name === name && h.id !== excludeId)

  const dateBodyTemplate = ({ rowData }) => {
    const isDateValid = DateTime.fromISO(rowData.date).isValid
    return (
      <div className="flex flex-column w-full">
        <UnifiedDatePicker
          className="w-full"
          dateRange={[
            isDateValid ? DateTime.fromISO(rowData.date) : null,
            isDateValid ? DateTime.fromISO(rowData.date) : null,
          ]}
          setDateRange={(e) => {
            clearEditionError(rowData.id)
            const isoDate = e[0].toISODate()
            if (isDateRepeated(isoDate, rowData.id)) {
              setEditionError(rowData.id, "La fecha ya existe")
              return
            }
            const updatedHolidays = holidays.map((holiday) =>
              holiday.id === rowData.id ?
                { ...holiday, date: isoDate }
              : holiday
            )
            setHolidays(updatedHolidays)
            onChange(updatedHolidays)
            setEditingDateCell(null)
          }}
          datePickerProps={{
            className: "w-full",
            autoFocus: true,
            minDate: DateTime.local().set({ year }).startOf("year"),
            maxDate: DateTime.local().set({ year }).endOf("year"),
            ref: datePickerRef,
            disabledDate: (current) => {
              return (
                allHolidays.some(
                  (h) => h.date && compareDates(h.date, current)
                ) || isDateAtWeekend(current)
              )
            },
          }}
        />
        {errors.edition[rowData.id] ?
          <ErrorMessageForm errorMessage={errors.edition[rowData.id]} />
        : null}
      </div>
    )
  }

  const handleDeleteRow = (rowData) => {
    const updatedHolidays = holidays.filter((h) => h.id !== rowData.id)
    setHolidays(updatedHolidays)
    onChange(updatedHolidays)
  }

  const handleAddNewHoliday = ({ holiday = newHoliday }) => {
    clearCreationError()

    if (!holiday.name || !holiday.date) {
      return
    }
    if (isDateRepeated(holiday.date)) {
      setCreationError("La fecha ya existe")
      return
    }
    if (isNameRepeated(holiday.name)) {
      setCreationError("Este festivo ya existe")
      return
    }
    const updatedHolidays = [
      ...holidays,
      { ...holiday, id: `aux-id-${holidays.length}` },
    ]
    setHolidays(updatedHolidays)
    onChange(updatedHolidays)
    setNewHoliday(DEFAULT_NEW_HOLIDAY)
    setTimeout(() => {
      tableRef?.current
        ?.getTable()
        ?.querySelector("tbody tr:last-child")
        .scrollIntoView({ block: "start", behavior: "smooth" })
    }, 0)
  }

  const deleteButtonTemplate = (rowData) => {
    if (rowData.isOfficial) return
    return (
      <Button
        icon="pi pi-trash"
        className="delete-holiday-btn p-button-text"
        onClick={() => handleDeleteRow(rowData)}
      />
    )
  }

  const showDeleteButton = () => {
    return (
      deletableRows &&
      holidays.length > 0 &&
      holidays.some((h) => !h.isOfficial)
    )
  }
  const footerColumnGroup = () => (
    <ColumnGroup>
      <RowColumnGroup>
        <Column
          footer={
            <InputTextForm
              placeholder={newValuePlaceholder}
              className="w-full"
              value={newHoliday.name}
              onChange={(e) => {
                clearCreationError()
                setNewHoliday((prev) => ({
                  ...prev,
                  name: e.target.value,
                }))
              }}
              onBlur={handleAddNewHoliday}
              error={errors.creation && [{ errorMessage: errors.creation }]}
            />
          }
        />
        <Column
          footer={
            <UnifiedDatePicker
              className="w-full"
              dateRange={[newHoliday.date, newHoliday.date]}
              setDateRange={(e) => {
                clearCreationError()
                const isoDate = e[0].toISODate()
                if (isDateRepeated(isoDate)) {
                  setCreationError("La fecha ya existe")
                  return
                }
                setNewHoliday((prev) => ({
                  ...prev,
                  date: e[0],
                }))
                handleAddNewHoliday({
                  holiday: { ...newHoliday, date: e[0] },
                })
              }}
              datePickerProps={{
                placeholder: "Selecciona una fecha",
                minDate: DateTime.local().set({ year }).startOf("year"),
                maxDate: DateTime.local().set({ year }).endOf("year"),
                disabledDate: (current) => {
                  return (
                    allHolidays.some(
                      (h) => h.date && compareDates(h.date, current)
                    ) || isDateAtWeekend(current)
                  )
                },
              }}
            />
          }
        />
        {showDeleteButton() ?
          <Column
            body={deleteButtonTemplate}
            style={{ width: "10%" }}
            colSpan={1}
          />
        : null}
      </RowColumnGroup>
    </ColumnGroup>
  )

  return (
    <DataTable
      ref={tableRef}
      className="holidays-table"
      value={holidays}
      dataKey="id"
      loading={loading}
      style={{ width: "100%" }}
      scrollable
      responsiveLayout="scroll"
      footerColumnGroup={footerColumnGroup()}
      loadingConfig={{
        qtyOfRows: 7,
      }}
      editMode="cell"
    >
      <Column
        field="name"
        header="Nombre"
        colSpan={2}
        onCellEditComplete={(e) => {
          if (isNameRepeated(e.value, e.rowData.id) || !e.value) {
            setErrorMessage({
              title: "Error",
              message:
                isNameRepeated(e.value, e.rowData.id) ?
                  "Este festivo ya existe"
                : "No puedes tener un festivo con nombre vacío",
            })
            setHolidays((prev) =>
              prev.map((holiday) =>
                holiday.id === e.rowData.id ?
                  { ...holiday, name: e.newValue }
                : holiday
              )
            )
            return
          }
          onChange(holidays)
        }}
        onCellEditCancel={(e) => {
          const restoredHolidays = holidays.map((holiday) =>
            holiday.id === e.rowData.id ?
              { ...holiday, name: e.newValue }
            : holiday
          )
          setHolidays(restoredHolidays)
        }}
        editor={(options) => {
          if (
            options.rowData.holidayTypeId ===
              parseInt(HOLIDAYS_MODALS_TABS.OFICIALES) &&
            options.rowData.isOfficial
          ) {
            return options.value
          }
          return (
            <InputTextForm
              className="w-full"
              value={
                holidays.filter((h) => h.id === options.rowData.id)[0].name
              }
              onChange={(e) => {
                clearEditionError(options.rowData.id)
                const updatedHolidays = holidays.map((holiday) =>
                  holiday.id === options.rowData.id ?
                    { ...holiday, name: e.target.value }
                  : holiday
                )
                setHolidays(updatedHolidays)
              }}
              onBlur={() => {
                if (!options.rowData.name) {
                  setErrorMessage({
                    title: "Error",
                    message: "El nombre es obligatorio",
                  })
                }
              }}
            />
          )
        }}
      />
      <Column
        field="date"
        header="Día libre"
        body={(rowData) => {
          if (!editingDateCell || editingDateCell !== rowData.id) {
            const isoDate = DateTime.fromISO(rowData.date)
            const formattedReadOnlyDate =
              isoDate.isValid ? isoDate.toFormat("dd/MM/yyyy") : "Sin fecha"
            return (
              <div
                className={clsx(
                  "read-only-date-picker",
                  "flex align-items-center w-full",
                  !isoDate.isValid ? "clr-secondary" : ""
                )}
                onClick={() => {
                  setEditingDateCell(rowData.id)
                  setTimeout(() => {
                    datePickerRef.current?.nativeElement.click()
                  }, 50)
                }}
              >
                {formattedReadOnlyDate}
              </div>
            )
          }
          if (editingDateCell === rowData.id) {
            return dateBodyTemplate({ rowData })
          }
        }}
        colSpan={2}
      />
      {showDeleteButton() ?
        <Column
          body={deleteButtonTemplate}
          style={{ maxWidth: "10%" }}
          colSpan={1}
        />
      : null}
    </DataTable>
  )
}

export default HolidaysTable

HolidaysTable.propTypes = {
  value: PropTypes.array,
  onChange: PropTypes.func,
  deletableRows: PropTypes.bool,
  newValuePlaceholder: PropTypes.string,
  loading: PropTypes.bool,
}
